import {
  DataGridPro,
  DataGridProProps,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  GridValidRowModel,
  MuiEvent,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import AurPagination from '../aur-pagination/AurPagination';
import React, {useState, memo, useMemo, useEffect, useCallback} from 'react';
import AurCustomSort from './AurCustomSort';
import {ReactComponent as SortIcon} from '../../../assests/svg/sorting_arrow.svg';
import {SortDirection} from '../../../types/types';
import AurCheckbox from '../aur-checkbox/AurCheckbox';
import {IconButton} from '@mui/material';
import AurSearchBox from '../aur-search-box/AurSearchBox';
import {
  AurDataGridFilterState,
  AurDataGridServerSideEvents,
} from './useServerSideDataGrid';
import AurDataGridNoRowsOverlay from '../aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import AurDataGridLoadingOverlay from '../aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import AurDataGridErrorOverlay from '../aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {AurLightTooltip} from '../aur-light-tooltip/aur-light-tooltip';
import AurTooltip from '../aur-tooltip/AurTooltip';
import {t} from 'i18next';
import AurIcon, {IconNames} from '../aur-icon/AurIcon';
import {scrollToGridView} from '../../../utils/utils';

type AurDataGridProProps = DataGridProProps & {
  entityType?: string;
  checkBoxVisible?: boolean;
  className?: string;
  cellClassName?: string;
  selectedIds?: (number | string)[];
  rowLimitForPaginationHide?: number; // row count exceeding this limit will make pagination visible
  onSelectionChange?: (selectedIds: (number | string)[]) => void;
  defaultSelectedtPage?: number; // initially selected page
  defaultItemsPerPage?: number; // initially selected number from dropdown
  hideFooterSelectedRowCount?: boolean; // true => won't show total selected rows count
  defaultSortColumnName?: string;
  defaultSortOrder?: 'asc' | 'desc';
  onPageChange?: (pagenumber: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  handleRowClick?: (
    params: GridRowParams,
    event?: MuiEvent<React.MouseEvent>
  ) => void; // to handle row click
  pointer?: boolean;
  primaryKey?: string; // Unique key property name per row
  setSortModel?: (sortModel: GridSortModel) => void;
  includeCancelContinueEvents?: boolean;
  showPagination?: boolean;
  onSearch?: (searchTerm: string) => void;
  noRowsOverlay?: React.JSXElementConstructor<any>; // custom overlay to show when there is no rows
  noRowOverlayHeight?: number;
  noRowOverlayClass?: string;
  isReqLoading?: boolean;
  haveReqError?: boolean;
  onErrorRefresh?: () => void;
  // Required props for server side data grid
  // useServerSideDataGrid hook return these following items
  enableServerSide?: boolean;
  totalRowCount?: number;
  filterState?: AurDataGridFilterState;
  serverSideEvents?: AurDataGridServerSideEvents;
  hideSortIcons?: boolean;
  visibleToolTip?: boolean;
  enableRowClickOnEnterPress?: boolean;
  showSortIconOnHover?: boolean;
  disableCellHeaderFocus?: boolean;
  applyFilterForReason?: boolean;
  currentPage?: number;
  itemsPerPage?: number;
  addedInDialog?: boolean; // to track datagrid visible on Dialog then use internal grid scroll when page change
  classNameToFindElement?: string; // this used to find grid using class selector to scroll on page change
  classNameOfParentScollElement?: string; // In dialog, scroll control by parent component so need to fire scroll on it when page change
};

export type CustomGridColDef<T extends GridValidRowModel = GridValidRowModel> =
  GridColDef<T> & {
    disableTooltip?: boolean; // added this to disable tooltip for selected column cell
    isDateColumn?: boolean;
  };

const AurDataGridPro = (props: AurDataGridProProps) => {
  const {
    rows = [],
    columns,
    pageSizeOptions = [],
    entityType,
    checkBoxVisible = true,
    className,
    cellClassName,
    selectedIds,
    rowLimitForPaginationHide = 0,
    defaultSelectedtPage = 1,
    defaultItemsPerPage = 10,
    hideFooterSelectedRowCount = true,
    defaultSortColumnName = '',
    defaultSortOrder = 'asc',
    totalRowCount = 0,
    pointer = false,
    sortModel,
    primaryKey = 'id', // Unique key property name per row. Default is "id"
    includeCancelContinueEvents = false,
    showPagination = true,
    filterDebounceMs = 0,
    enableServerSide = false,
    onSelectionChange,
    handleRowClick,
    onPageChange,
    onPageSizeChange,
    setSortModel,
    onSearch,
    noRowsOverlay,
    filterState,
    serverSideEvents,
    noRowOverlayHeight,
    noRowOverlayClass,
    isReqLoading,
    haveReqError,
    onErrorRefresh,
    hideSortIcons = true,
    visibleToolTip = false,
    enableRowClickOnEnterPress,
    showSortIconOnHover = false,
    disableCellHeaderFocus = false,
    applyFilterForReason = false,
    addedInDialog = false,
    classNameToFindElement,
    classNameOfParentScollElement,
    ...restProps
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // For dropdown
  const [activeColumn, setActiveColumn] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(defaultSelectedtPage);
  const [itemsPerPage, setItemsPerPage] = useState<number>(defaultItemsPerPage);
  const totalItems = totalRowCount ? totalRowCount : rows.length;
  const previousPage = React.useRef(restProps.currentPage);
  const previousItemPerPage = React.useRef(restProps.itemsPerPage);
  let shouldHidePagination = false;
  const MemoCheckbox = React.memo(AurCheckbox);
  const renderCheckbox = useCallback(
    (props: any) => <MemoCheckbox {...props} />,
    []
  );
  const gridApiRef = useGridApiRef();

  useEffect(() => setItemsPerPage(defaultItemsPerPage), [defaultItemsPerPage]);

  useEffect(() => {
    let isPageChange = false;
    if (showPagination) {
      // if true then pagination value manage internally so refer local state variable
      isPageChange =
        previousPage.current !== currentPage ||
        previousPage.current !== itemsPerPage;
    } else {
      // Pagination value managed from parent component so refer the prop value for it
      isPageChange =
        previousPage.current !== restProps.currentPage ||
        previousPage.current !== restProps.itemsPerPage;
    }
    if (isPageChange && rows.length > 0) {
      previousPage.current = showPagination
        ? currentPage
        : restProps.currentPage;
      previousItemPerPage.current = showPagination
        ? itemsPerPage
        : restProps.itemsPerPage;

      if (addedInDialog) {
        // If showing in dialog then need to fine scrolling container and scroll it as
        // we can't perform scroll on window when dialog open
        const parentScrollElement = document.querySelector(
          `.${classNameOfParentScollElement}`
        ) as HTMLElement | null;
        // Added time out to make sure grid updated correclty with data before scroll
        setTimeout(() => {
          requestAnimationFrame(() => {
            parentScrollElement?.scrollTo({
              top: 0,
              behavior: 'auto',
            });
          });
        }, 300);
        return;
      }
      scrollToGridView(classNameToFindElement ?? 'aur-datagrid', true);
    }
  }, [rows]);

  if (enableServerSide) {
    shouldHidePagination =
      totalRowCount === 0 ||
      (rowLimitForPaginationHide != 0 &&
        totalRowCount <= rowLimitForPaginationHide);
  } else {
    shouldHidePagination =
      rows.length === 0 ||
      (rowLimitForPaginationHide != 0 &&
        rows.length <= rowLimitForPaginationHide);
  }

  const perPageOptions = useMemo(() => {
    const options = (pageSizeOptions as number[]).filter(
      (option) => option <= totalItems
    );
    const additionalOption = (pageSizeOptions as number[]).find(
      (option) => option >= totalItems
    );
    if (additionalOption && !options.includes(additionalOption)) {
      options.push(additionalOption);
    }
    return options;
  }, [pageSizeOptions, totalItems]);

  const CustomPagination: React.FC = () => {
    const apiRef = useGridApiContext();
    if (enableServerSide && filterState) {
      apiRef.current.setPage(filterState.paginationState.page - 1);
      setCurrentPage(filterState.paginationState.page);
    }

    const handlePageChange = (page: number) => {
      apiRef.current.setPage(page - 1);
      setCurrentPage(page);
      onPageChange?.(page);
      serverSideEvents &&
        serverSideEvents.handlePaginationChange({
          page: page,
          pageSize: itemsPerPage,
        });
    };

    const setItemsPerPageHandler = (itemsPerPage: number) => {
      setItemsPerPage(itemsPerPage);
      onPageSizeChange?.(itemsPerPage);
      apiRef.current.setPage(0);
      setCurrentPage(1);
      onPageChange?.(1);
      apiRef.current.setPageSize(itemsPerPage);
      serverSideEvents &&
        serverSideEvents.handlePaginationChange({
          page: 1,
          pageSize: itemsPerPage,
        });
    };

    return totalItems > 0 && showPagination ? (
      <AurPagination
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        perPageOptions={perPageOptions}
        itemType={entityType}
        setItemsPerPage={setItemsPerPageHandler}
        onPageChange={handlePageChange}
        includeCancelContinueEvents={includeCancelContinueEvents}
      />
    ) : (
      <div></div>
    );
  };

  // Function to handle sorting
  const handleSort = (field: string, direction: SortDirection) => {
    serverSideEvents &&
      serverSideEvents.handleSortChange([{field: field, sort: direction}]);
    setSortModel ? setSortModel([{field, sort: direction || null}]) : '';
    setAnchorEl(null);
  };

  // Custom header renderer for sorting
  const renderSortHeader = (params: any) => {
    const handleOpenSortDropdown = (event: any, column: string) => {
      setAnchorEl(event.currentTarget);
      setActiveColumn(column);
    };

    const headerNameSpan = (
      <span
        className={
          params.colDef.isSearchBoxRequired
            ? 'searchbox-column-headername'
            : 'sort-column-headername'
        }
        tabIndex={disableCellHeaderFocus ? -1 : 0}
      >
        {params.colDef.headerName}
      </span>
    );

    return (
      <>
        <div
          className={
            params.colDef.field === 'select'
              ? 'aur-datagrid-checkbox-separator'
              : params.colDef.isBorderNotRequired
                ? ''
                : params.colDef.isSearchBoxRequired
                  ? 'sort-img-header-div aur-datagrid-search-box-column-separator'
                  : 'sort-img-header-div aur-datagrid-column-separator'
          }
        >
          {params.colDef.showHeaderTooltip ? (
            <AurLightTooltip title={params.colDef.headerName}>
              {headerNameSpan}
            </AurLightTooltip>
          ) : (
            headerNameSpan
          )}
          {params.colDef.isSearchBoxRequired && (
            <div className="sort-column-searchbox">
              <AurSearchBox onSearch={onSearch} />
            </div>
          )}

          {params.colDef.showInfo && (
            <>
              <AurTooltip
                title={t(params.colDef.infoMessage)}
                aria-label={
                  params.colDef.label
                    ? `${t(params.colDef.label)} ${t(params.colDef.infoMessage)}`
                    : t(params.colDef.infoMessage)
                }
                className="typo-text tooltipMessage_size"
                arrowPosition={'top'}
                tabIndex={disableCellHeaderFocus ? -1 : 0}
                trigger={
                  params.colDef.showInfoTooltipOnClick ? 'click' : 'hover'
                }
              >
                <span className="tooltip-info-icon">
                  <AurIcon icon={IconNames.Info} />
                </span>
              </AurTooltip>
              {params.colDef.sortable ? (
                <span className="between-space" />
              ) : null}
            </>
          )}

          {params.colDef.sortable ? (
            <IconButton
              onClick={(event) =>
                handleOpenSortDropdown(event, params.colDef.field)
              }
              aria-label="sort"
              className={`custom-icon-button 
                ${showSortIconOnHover ? 'sort-icon-button' : ''} 
                ${activeColumn === params.colDef.field && anchorEl ? 'menu-opened' : ''}
              `}
            >
              <SortIcon className="custom-sort-icon" aria-hidden="true"/>
            </IconButton>
          ) : (
            ''
          )}
        </div>

        {activeColumn === params.colDef.field && (
          <AurCustomSort
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            onSort={(direction) => handleSort(params.colDef.field, direction)}
            sortModel={sortModel}
            activeColumn={activeColumn}
          />
        )}
      </>
    );
  };

  const withTabIndex = (
    renderCell: (params: GridRenderCellParams) => React.ReactNode
  ) => {
    return (params: GridRenderCellParams) => {
      const cellContent = renderCell(params);

      return visibleToolTip &&
        ((params.colDef as CustomGridColDef).disableTooltip ?? false) ===
          false ? (
        <AurLightTooltip
          title={
            (params.colDef as CustomGridColDef).isDateColumn
              ? t('common.yearMonthDate')
              : cellContent
          }
        >
          <span>{cellContent}</span>
        </AurLightTooltip>
      ) : (
        <span className={cellClassName}>{cellContent}</span>
      );
    };
  };

  const renderCellWithTabIndex =
    (column: CustomGridColDef) => (params: GridRenderCellParams) => {
      const {value, row} = params;
      let formattedValue = value;

      formattedValue = column.valueFormatter
        ? column.valueFormatter(
            formattedValue as never,
            row,
            column,
            gridApiRef
          )
        : formattedValue;

      const cellContent = (
        <span className="cell-truncate">
          {formattedValue}
        </span>
      );

      return visibleToolTip ? (
        <AurLightTooltip
          title={
            column?.isDateColumn ? t('common.yearMonthDate') : formattedValue
          }
        >
          {cellContent}
        </AurLightTooltip>
      ) : (
        cellContent
      );
    };

  // Modify columns to add the custom sort dropdown and disable the sort button
  const modifiedColumns: any = columns.map((column) => ({
    ...column,
    renderCell: column.renderCell
      ? withTabIndex(column.renderCell)
      : renderCellWithTabIndex(column),
    ...(hideSortIcons
      ? {
          renderHeader: column.renderHeader ?? renderSortHeader,
          hideSortIcons: true,
        }
      : {}),
  }));

  const onFilterModelChange = (
    filterModel: any,
    details: GridCallbackDetails
  ) => {
    // This prevents the filter model from updating for no reason which calls the API excessively
    if (applyFilterForReason && !details.reason) {
      return;
    }

    serverSideEvents &&
      serverSideEvents.handleFilterChange(filterModel, filterDebounceMs);
  };
  const noDataGridOverlay = useMemo(() => {
    if (isReqLoading) return AurDataGridLoadingOverlay;
    if (haveReqError) return AurDataGridErrorOverlay;
    if (noRowsOverlay) return noRowsOverlay;
    return AurDataGridNoRowsOverlay;
  }, [isReqLoading, haveReqError]) as React.JSXElementConstructor<unknown>;

  const handleCellKeyDown = (_: GridCellParams, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && enableRowClickOnEnterPress) {
      event.preventDefault(); // Prevent the default Enter behavior
      /**
       * On Press of enter key, find active row and fire the onclick event for it
       */
      const focusedRow = document.activeElement?.closest(
        '.MuiDataGrid-row'
      ) as HTMLDivElement;
      if (focusedRow) {
        focusedRow.click();
      }
    }
  };

  return (
    <DataGridPro
      pagination
      loading={isReqLoading}
      rowCount={totalRowCount}
      filterDebounceMs={filterDebounceMs}
      onFilterModelChange={onFilterModelChange}
      // onSortModelChange={onSortModelChange}
      className={`aur-datagrid ${className} ${
        showSortIconOnHover ? 'aur-sort-hover' : ''
      }`}
      rows={rows}
      getRowId={(row) => row[primaryKey]}
      columns={modifiedColumns}
      checkboxSelection={checkBoxVisible}
      sortModel={sortModel} // Apply sort model to DataGrid
      disableColumnMenu
      hideFooter={shouldHidePagination || !showPagination}
      slots={{
        pagination: CustomPagination, // AurPagination component
        baseCheckbox: renderCheckbox,
        noRowsOverlay: noDataGridOverlay,
        loadingOverlay: () => <AurDataGridLoadingOverlay />,
        noResultsOverlay: noRowsOverlay ?? AurDataGridNoRowsOverlay,
      }}
      slotProps={{
        noRowsOverlay: {
          containerHeight: noRowOverlayHeight,
          containerClass: noRowOverlayClass,
          onRefresh: onErrorRefresh,
          enableParentWrapper: false,
        } as unknown as Record<string, unknown>,
        noResultsOverlay: {
          containerHeight: noRowOverlayHeight,
          containerClass: noRowOverlayClass,
        } as unknown as Record<string, unknown>,
      }}
      onRowSelectionModelChange={(newSelection) => {
        onSelectionChange?.(newSelection as number[]);
      }}
      keepNonExistentRowsSelected
      rowSelectionModel={selectedIds}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: itemsPerPage, // set initial page size
          },
        },
        sorting: {
          sortModel: [{field: defaultSortColumnName, sort: defaultSortOrder}], // set default column order
        },
      }}
      hideFooterSelectedRowCount={hideFooterSelectedRowCount}
      onRowClick={handleRowClick}
      sx={{cursor: pointer ? 'pointer' : 'default'}}
      filterMode={enableServerSide ? 'server' : 'client'}
      paginationMode={enableServerSide ? 'server' : 'client'}
      sortingMode={enableServerSide ? 'server' : 'client'}
      onCellKeyDown={handleCellKeyDown}
      {...restProps}
    />
  );
};

export default memo(AurDataGridPro);
